import { useEffect } from 'react'
import { useLocation } from 'react-router'
import { useTranslation } from 'react-i18next'
import { AppHeader } from './AppHeader'
import { AppSidebar } from './AppSidebar'
import { NAV_ITEMS } from '../../constants/navigation'
import { RouteLoadingProvider } from '../../contexts/RouteLoadingContext'
import { TopProgressBar } from '../ui/TopProgressBar'
import { HomeBackButton } from '../HomeBackButton'
import { isAggregateTenantRoute } from '../../constants/aggregateTenantRoutes'
import { useTenant } from '../../contexts/TenantContext'
import { useBrandedText, useBrandName } from '../../contexts/UiCustomizationContext'
import { useAppMode } from '../../contexts/AppModeContext'
import { useActiveApp } from '../../contexts/ActiveAppContext'
import { isMonitoringTracesPath } from '../../features/agent-studio/utils/studioObservabilityPath'
import { storeHeaderName } from './storeHeaderName'
import { isManageStorePath, isStorePath } from '../../utils/appSuites/storePaths'
import './AppLayout.css'
import type { TFunction } from 'i18next'

function isFullWidthTracesRoute(pathname: string): boolean {
  return pathname.includes('/agent-studio/traces') || isMonitoringTracesPath(pathname)
}

function resolveNavItemPageTitle(currentPath: string, t: TFunction): string {
  const exactNavItem = NAV_ITEMS.find((item) => item.path === currentPath)
  if (exactNavItem) {
    return t(exactNavItem.labelKey)
  }

  const prefixNavItem = NAV_ITEMS
    .filter((item) => item.path !== '/' && currentPath.startsWith(item.path))
    .sort((a, b) => b.path.length - a.path.length)[0]
  return prefixNavItem ? t(prefixNavItem.labelKey) : t('nav.home')
}

function resolveAppLayoutPageTitle(currentPath: string, search: string, t: TFunction): string {
  if (currentPath.startsWith('/model-chat/model-arena')) {
    return t('nav.chat') + ' - ' + t('chat.modelArena.title')
  }
  if (currentPath.startsWith('/model-chat')) {
    return t('nav.chat')
  }
  if (
    currentPath.startsWith('/prompts/') &&
    (currentPath.includes('/edit') || currentPath.includes('/view'))
  ) {
    return t('nav.prompts')
  }
  if (currentPath.startsWith('/monitoring')) {
    return t('nav.monitoring', 'Monitoring')
  }
  if (
    currentPath.startsWith('/agent-registry/guardrails') &&
    new URLSearchParams(search).get('from') === 'monitoring'
  ) {
    return t('nav.monitoring', 'Monitoring')
  }
  
  const fromParam = new URLSearchParams(search).get('from')
  if (currentPath.includes('/agent-studio/traces') || currentPath.includes('/agent-studio/logs') || currentPath.includes('/agent-studio/chat')) {
    if (fromParam === '/monitoring' || fromParam === 'monitoring') {
      return t('nav.monitoring', 'Monitoring')
    }
    if (fromParam === '/agent-registry' || fromParam === 'registry') {
      return t('nav.registry', 'Registry')
    }
  }
  if (currentPath.startsWith('/platform-admin/tenants/')) {
    return t('nav.platformAdmin')
  }
  if (currentPath.startsWith('/build')) {
    return 'AI Workflow Builder'
  }
  if (currentPath.startsWith('/agent-workflows/ai-builder')) {
    return 'AI Workflow Builder'
  }
  if (currentPath.startsWith('/agent-studio/ai-builder')) {
    return 'AI Agent Builder'
  }

  return resolveNavItemPageTitle(currentPath, t)
}

export function AppLayout({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation()
  const brandText = useBrandedText()
  const location = useLocation()
  const { isApps, experienceResolving } = useAppMode()
  const { activeApp } = useActiveApp()
  const { name: brandName } = useBrandName()
  const { currentTenant, homeTenant, isPlatformAdmin, switchTenant } = useTenant()

  // When a platform admin navigates away from aggregate routes while "All Tenants" is
  // selected (currentTenant === null), auto-revert to their home tenant so other
  // pages always have a concrete tenant context.
  useEffect(() => {
    if (
      !isAggregateTenantRoute(location.pathname) &&
      !currentTenant &&
      isPlatformAdmin &&
      homeTenant
    ) {
      switchTenant(homeTenant.id, homeTenant)
    }
  }, [location.pathname, currentTenant, isPlatformAdmin, homeTenant, switchTenant])

  // Update page title in header
  useEffect(() => {
    const pageTitle = resolveAppLayoutPageTitle(location.pathname, location.search, t)
    const titleElement = document.getElementById('page-title')
    if (titleElement) {
      titleElement.textContent = pageTitle
    }
  }, [location.pathname, location.search, t])

  // Active migration project session: full-width workspace (no main left nav), like agent canvas.
  // Allow any depth of sub-routes (e.g. /profiler, /profiler/schema-map, /profiler/foo/bar).
  const migrationSessionMatch = location.pathname.match(
    /^\/ontology\/migrations\/([^/]+)(\/.*)?$/,
  )
  const isMigrationProjectSession =
    Boolean(
      migrationSessionMatch &&
        migrationSessionMatch[1] !== 'new' &&
        migrationSessionMatch[1] !== 'de-tools',
    )

  // Benchmark drill-down: benchmarks?benchmark_type=<type> (Dataset tab)
  //                   or  benchmarks?session_type=<type>   (Sessions tab)
  // The URL stays at /benchmarks (formerly /evals-simulations) so we detect by search params.
  const layoutSearchParams = new URLSearchParams(location.search)
  const isBenchmarkDrillDown =
    (location.pathname === '/benchmarks' || location.pathname === '/evals-simulations') &&
    (!!layoutSearchParams.get('benchmark_type') || !!layoutSearchParams.get('session_type'))
  // Evaluations → Eval Runs → one agent/workflow (Sessions / Analytics / Metrics / Insights):
  // focused full-width view, like the other drill-downs.
  const isEvalSubjectDrillDown =
    location.pathname === '/evals-simulations' &&
    (!!layoutSearchParams.get('agent_id') || !!layoutSearchParams.get('workflow_id'))

  // Graph quality evals for one graph: a focused full-width report, like the
  // drill-downs above.
  const isGraphEvalsDrillDown = /^\/data\/graph\/[^/]+\/evals\/?$/.test(location.pathname)

  // Hide sidebar for specific routes (not /dip — main nav + Milkyway/platform switch must stay visible)
  // The launcher and store pages are sidebar-free in Four apps. While the experience is
  // still resolving, `isApps` is not true yet — hide the rail there too, otherwise a rail
  // flashes in on refresh and disappears once Four apps resolves.
  const hideSidebar =
    ((isApps || experienceResolving) &&
      (location.pathname === '/' || isStorePath(location.pathname))) ||
    isManageStorePath(location.pathname) ||
    isMigrationProjectSession ||
    isBenchmarkDrillDown ||
    isEvalSubjectDrillDown ||
    isGraphEvalsDrillDown ||
    location.pathname.startsWith('/benchmarks/glossary') ||
    location.pathname.startsWith('/benchmarks/huggingface') ||
    location.pathname.startsWith('/evals-simulations/huggingface') ||
    location.pathname.startsWith('/agent-registry/guardrails') ||
    location.pathname.includes('/runtime-settings') ||
    location.pathname.startsWith('/resources/docs') ||
    location.pathname.startsWith('/build') ||
    location.pathname.startsWith('/admin/roles') ||
    location.pathname.startsWith('/model-chat') ||
    (location.pathname.startsWith('/platform-admin/') &&
      location.pathname.replace(/\/+$/, '') !== '/platform-admin') ||
    (location.pathname.startsWith('/agent-workflows') &&
      location.pathname !== '/agent-workflows') ||
    (location.pathname.startsWith('/projects/') && location.pathname !== '/projects') ||
    (location.pathname.startsWith('/prompts/') &&
      (location.pathname.includes('/edit') ||
        location.pathname.includes('/view') ||
        location.pathname.includes('/playground'))) ||
    (location.pathname.startsWith('/tools/') && location.pathname.includes('/view')) ||
    (location.pathname.startsWith('/solutions/') && location.pathname.includes('/editor')) ||
    location.pathname.startsWith('/ontology/semantic-hub/models/') ||
    location.pathname.startsWith('/data/graph-preview') ||
    /^\/data\/graph\/[^/]+\/approvals/.test(location.pathname) ||
    (location.pathname.startsWith('/skills/') && location.pathname.length > 8) ||
    isFullWidthTracesRoute(location.pathname) ||
    location.pathname.includes('/agent-studio/logs') ||
    location.pathname.includes('/agent-studio/canvas') ||
    location.pathname.includes('/agent-studio/view') ||
    location.pathname.includes('/agent-studio/chat') ||
    location.pathname.includes('/agent-studio/self-heal') ||
    location.pathname.includes('/agent-registry/self-heal') ||
    location.pathname.includes('/monitoring/self-heal') ||
    location.pathname.includes('/agent-registry/chat') ||
    location.pathname.includes('/agent-studio/ai-builder') ||
    location.pathname.includes('/ontology/ai-builder') ||
    /^\/agent-store\/[^/]+\/chat/.test(location.pathname) ||
    /^\/agent-store\/[^/]+\/about/.test(location.pathname) ||
    /^\/workflow-store\/[^/]+\/about/.test(location.pathname) ||
    /^\/ontology\/data-models\/[^/]+$/.test(location.pathname) ||
    /^\/blueprints\/[^/]+/.test(location.pathname) ||
    /^\/ideate\/blueprint\/[^/]+/.test(location.pathname) ||
    /^\/benchmark-studio\/sessions\/[^/]+/.test(location.pathname)

  // Hide app header for full-screen canvas/view routes that have their own toolbar
  // These routes render CanvasView (position: fixed, z-index: 1000) which has its own CanvasToolbar.
  // The AppHeader at z-index: 1001 would cover the CanvasToolbar, hiding it.
  // Migration project session (overview + agent pages): decluttered workspace with local chrome
  // (MigrationSessionTopBar: home + pipeline + tenant) — same match as hideSidebar.
  const hideHeader =
    isMigrationProjectSession ||
    location.pathname.includes('/agent-studio/canvas') ||
    location.pathname.includes('/agent-studio/view') ||
    location.pathname.includes('/agent-studio/chat') ||
    location.pathname.includes('/agent-registry/chat') ||
    // Agent Store chat reuses the full-screen Agent Studio chat UI (ChatView);
    // hide the global header so its own header + session panel get the full viewport,
    // matching the Agent Studio published-chat experience.
    /^\/agent-store\/[^/]+\/chat/.test(location.pathname)

  // Browser tab title when AppHeader is hidden (no #page-title element).
  useEffect(() => {
    if (!isApps || !activeApp || isMigrationProjectSession) return undefined
    const previous = document.title
    const productName = storeHeaderName(location.pathname, t) ?? activeApp.name
    document.title = `${brandName} ${productName}`
    return () => {
      document.title = previous
    }
  }, [isApps, activeApp, brandName, isMigrationProjectSession, location.pathname, t])

  useEffect(() => {
    if (!isMigrationProjectSession) return
    const prev = document.title
    const slug = location.pathname.replace(/^\/ontology\/migrations\/[^/]+/, '').replace(/^\//, '')
    const parts = slug.split('/').filter(Boolean)
    const tail =
      parts.length > 0
        ? parts
            .map((p) =>
              p
                .split('-')
                .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
                .join(' '),
            )
            .join(' · ')
        : 'Project'
    document.title = `${tail} · ${brandText(t('nav.brandPlatform'))}`
    return () => {
      document.title = prev
    }
  }, [location.pathname, isMigrationProjectSession, t, brandText])

  return (
    <RouteLoadingProvider>
      <div
        className={`app-frame${isApps ? ' app-frame--apps' : ''}`}
        style={isApps && activeApp ? { '--app-accent': activeApp.accent_color } as React.CSSProperties : undefined}
      >
        <TopProgressBar />
        <div className={`app-shell${hideHeader ? ' app-shell--no-header' : ''}`}>
          {!hideHeader && <AppHeader />}
          <div className="app-content">
            {!hideSidebar && experienceResolving && (
              <aside className="nav-rail collapsed" aria-busy="true" />
            )}
            {!hideSidebar && !experienceResolving && <AppSidebar />}
            <main className={`workspace content${hideSidebar ? ' content--full-width' : ''}`}>
              <HomeBackButton />
              {children}
            </main>
          </div>
        </div>
      </div>
    </RouteLoadingProvider>
  )
}
